import type { Logger as PinoLogger } from 'pino'

const IS_DEV = process.env.NODE_ENV === 'development'
const IS_SERVER = typeof window === 'undefined'

// Opt out of using pino and use console instead
const USE_CONSOLE_LOG = false

type LogMethod = (...message: any[]) => void

type Logger = {
  info: LogMethod
  error: LogMethod
  log: LogMethod
  warn: LogMethod
}

export enum LogLevel {
  Info = 'info',
  Warn = 'warn',
  Error = 'error',
  Silent = 'silent',
}

type LoggerOptions = {
  level?: LogLevel
  showPrefix?: boolean
  showStacktrace?: boolean
  useConsole?: boolean
}

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  [LogLevel.Info]: 0,
  [LogLevel.Warn]: 1,
  [LogLevel.Error]: 2,
  [LogLevel.Silent]: 3,
}

const SILENT_LOGGER: Logger = {
  info: () => {},
  error: () => {},
  log: () => {},
  warn: () => {},
}

//# Pino setup (server only)
// The root pino instance is injected from lib/payload.ts after Payload initializes.
// This avoids a circular dependency (payload.ts imports createLogger).
// Before setServerLogger is called, server-side logs fall back to console.

let _rootLogger: PinoLogger | null = null

export function setServerLogger(pinoInstance: PinoLogger | null) {
  _rootLogger = pinoInstance
}

//# Shared helpers

const formatPrefix = (prefix: string): string => (prefix ? `[${prefix}]` : '')
const findError = (message: any[]): Error | undefined => message.find((m) => m instanceof Error)

function meetsLevel(msgLevel: LogLevel, minLevel: LogLevel): boolean {
  return LEVEL_PRIORITY[msgLevel] >= LEVEL_PRIORITY[minLevel]
}

const formatMessage = (prefix: string, message: any[]): string => {
  const parts = message.map((m) => {
    if (m instanceof Error) return m.message
    if (m === null || m === undefined) return String(m)
    if (typeof m === 'object') {
      try {
        return JSON.stringify(m)
      } catch {
        return String(m)
      }
    }
    return String(m)
  })
  const text = parts.join(' ')
  return prefix ? `${prefix} ${text}` : text
}

//# Client-side logger
function createClientLogger(prefix: string, options: LoggerOptions): Logger {
  const {
    showPrefix = true,
    showStacktrace = false,
    level = IS_DEV ? LogLevel.Info : LogLevel.Warn,
  } = options
  const logPrefix = showPrefix ? formatPrefix(prefix) : ''

  if (level === LogLevel.Silent) return SILENT_LOGGER

  function emit(msgLevel: LogLevel, consoleFn: typeof console.log, ...message: any[]) {
    if (!meetsLevel(msgLevel, level)) return

    if (logPrefix) {
      consoleFn(logPrefix, ...message)
    } else {
      consoleFn(...message)
    }

    if (msgLevel === 'error' && showStacktrace) {
      const stack = new Error().stack?.split('\n').slice(3).join('\n')
      console.error(stack)
    }
  }

  return {
    info: (...msg) => emit(LogLevel.Info, console.log, ...msg),
    error: (...msg) => emit(LogLevel.Error, console.error, ...msg),
    log: (...msg) => emit(LogLevel.Info, console.log, ...msg),
    warn: (...msg) => emit(LogLevel.Warn, console.warn, ...msg),
  }
}

//# Server-side logger
function createServerLogger(prefix: string, options: LoggerOptions): Logger {
  const { showPrefix = true, showStacktrace = false, level = LogLevel.Info } = options
  const logPrefix = showPrefix ? formatPrefix(prefix) : ''

  if (level === LogLevel.Silent) return SILENT_LOGGER

  const emitConsole = (consoleFn: typeof console.log, ...message: any[]) => {
    if (logPrefix) {
      consoleFn(logPrefix, ...message)
    } else {
      consoleFn(...message)
    }
  }

  const emit = (msgLevel: LogLevel, consoleFn: typeof console.log, ...message: any[]) => {
    if (!meetsLevel(msgLevel, level)) return

    // Fall back to console before setServerLogger is called (early startup)
    if (!_rootLogger) {
      emitConsole(consoleFn, ...message)
      return
    }

    const msg = formatMessage(logPrefix, message)
    const err = findError(message)

    if (err) {
      _rootLogger[msgLevel]({ err }, msg)
    } else {
      _rootLogger[msgLevel](msg)
    }

    if (msgLevel === LogLevel.Error && showStacktrace) {
      const stack = new Error().stack?.split('\n').slice(3).join('\n')
      if (stack) _rootLogger.error(stack)
    }
  }

  return {
    info: (...msg) => emit(LogLevel.Info, console.log, ...msg),
    error: (...msg) => emit(LogLevel.Error, console.error, ...msg),
    log: (...msg) => emit(LogLevel.Info, console.log, ...msg),
    warn: (...msg) => emit(LogLevel.Warn, console.warn, ...msg),
  }
}

//# Legacy logger (v1) — console-only, no pino dependency
function createLegacyLogger(prefix: string, options: LoggerOptions): Logger {
  const {
    showPrefix = true,
    showStacktrace = false,
    level = IS_DEV ? LogLevel.Info : LogLevel.Warn,
  } = options
  const logPrefix = showPrefix ? formatPrefix(prefix) : ''

  if (level === LogLevel.Silent) return SILENT_LOGGER

  function emit(msgLevel: LogLevel, consoleFn: typeof console.log, ...message: any[]) {
    if (!meetsLevel(msgLevel, level)) return

    if (logPrefix) {
      consoleFn(logPrefix, ...message)
    } else {
      consoleFn(...message)
    }

    if (msgLevel === 'error' && showStacktrace) {
      const stack = new Error().stack?.split('\n').slice(3).join('\n')
      console.error(stack)
    }
  }

  return {
    info: (...msg) => emit(LogLevel.Info, console.log, ...msg),
    error: (...msg) => emit(LogLevel.Error, console.error, ...msg),
    log: (...msg) => emit(LogLevel.Info, console.log, ...msg),
    warn: (...msg) => emit(LogLevel.Warn, console.warn, ...msg),
  }
}

/**
 * PUBLIC API
 * Creates a logger with the specified prefix.
 * Server-side: structured JSON via pino (queryable in Better Stack).
 * Client-side: console-based with prefix formatting.
 *
 * @param prefix - Module name, shown as `[prefix]` in messages and as structured `moduleName` field.
 * @param options.level - Minimum log level: 'info' | 'warn' | 'error' | 'silent'.
 *   Server default: 'info'. Client default: 'info' in dev, 'warn' in prod.
 * @param options.showPrefix - Prepend `[prefix]` to messages (default: true).
 * @param options.showStacktrace - Append extra call-site stack trace on errors (default: false).
 * @param options.useConsole - Force use of console instead of pino (default: false).
 */
export function createLogger(prefix: string = '', options: LoggerOptions = {}): Logger {
  if (USE_CONSOLE_LOG || options.useConsole) {
    // Preserve the server's Info default when falling back to legacy logger —
    // otherwise legacy's own `IS_DEV ? Info : Warn` default would silently drop
    // Info-level logs in prod for callers that opted into console output.
    const defaultLevel = IS_SERVER ? LogLevel.Info : IS_DEV ? LogLevel.Info : LogLevel.Warn
    return createLegacyLogger(prefix, { ...options, level: options.level ?? defaultLevel })
  }
  return IS_SERVER ? createServerLogger(prefix, options) : createClientLogger(prefix, options)
}
